import React from 'react';
import { ChevronRight, Plus, Award, Timer, Activity, Trophy, Users, Map, Calendar, TrendingUp } from 'lucide-react';

const ChallengeCard = ({ challenge, type, onJoin }) => {
  // Helper function to render the appropriate icon
  const renderIcon = () => {
    const iconMap = {
      Activity: <Activity size={24} className={`text-${type === 'active' ? 'primary' : type === 'available' ? 'secondary' : 'accent'}`} />,
      Trophy: <Trophy size={24} className={`text-${type === 'active' ? 'primary' : type === 'available' ? 'secondary' : 'accent'}`} />,
      Users: <Users size={24} className={`text-${type === 'active' ? 'primary' : type === 'available' ? 'secondary' : 'accent'}`} />,
      Timer: <Timer size={24} className={`text-${type === 'active' ? 'primary' : type === 'available' ? 'secondary' : 'accent'}`} />,
      TrendingUp: <TrendingUp size={24} className={`text-${type === 'active' ? 'primary' : type === 'available' ? 'secondary' : 'accent'}`} />,
      Map: <Map size={24} className={`text-${type === 'active' ? 'primary' : type === 'available' ? 'secondary' : 'accent'}`} />,
      Award: <Award size={24} className={`text-${type === 'active' ? 'primary' : type === 'available' ? 'secondary' : 'accent'}`} />,
      Calendar: <Calendar size={24} className={`text-${type === 'active' ? 'primary' : type === 'available' ? 'secondary' : 'accent'}`} />
    };
    
    // If the challenge has a custom icon component, use that; otherwise, look it up in the map
    if (challenge.icon) return challenge.icon;
    return iconMap[challenge.iconType] || <Activity size={24} className="text-primary" />;
  };
  
  // Determine the badge style based on challenge status/type
  const getBadgeStyle = () => {
    if (type === 'active') {
      return challenge.status === 'on-track' ? 'bg-green-100 text-green-800' : 
             challenge.status === 'behind' ? 'bg-red-100 text-red-800' : 
             'bg-yellow-100 text-yellow-800';
    } else if (type === 'available') {
      return 'bg-blue-100 text-blue-800';
    } else {
      return 'bg-green-100 text-green-800';
    }
  };
  
  // Determine the badge text based on challenge status/type
  const getBadgeText = () => {
    if (type === 'active') {
      return challenge.status === 'on-track' ? 'On Track' : 
             challenge.status === 'behind' ? 'Falling Behind' : 
             'Just Started';
    } else if (type === 'available') {
      return challenge.difficulty || 'Normal';
    } else {
      return 'Completed';
    }
  };
  
  // Determine if challenge is a special event
  const isSpecialEvent = challenge.type === 'event' || challenge.isSpecialEvent;
  
  return (
    <div className={`bg-white rounded-xl shadow-sm p-6 ${isSpecialEvent ? 'border-2 border-accent' : ''}`}>
      {isSpecialEvent && (
        <div className="mb-3 inline-block px-3 py-1 bg-accent text-white text-sm rounded-full">
          Special Event
        </div>
      )}
      
      <div className="flex items-start">
        <div className={`p-2 ${isSpecialEvent ? 'bg-accent bg-opacity-10' : 'bg-gray-100'} rounded-full mr-4`}>
          {renderIcon()}
        </div>
        <div className="flex-1">
          <div className="flex justify-between">
            <div>
              <h3 className="text-xl font-semibold">{challenge.title}</h3>
              <p className="text-gray-500">{challenge.description}</p>
            </div>
            <div className="text-right">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getBadgeStyle()}`}>
                {getBadgeText()}
              </span>
              <p className="text-sm text-gray-500 mt-1">
                {challenge.type === 'team' ? 'Team Challenge' : 
                 challenge.type === 'global' ? 'Global Challenge' :
                 challenge.type === 'event' ? 'Event Challenge' : 
                 'Individual Challenge'}
              </p>
            </div>
          </div>
          
          <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Dates</p>
              <p>{challenge.startDate} - {challenge.endDate}</p>
            </div>
            <div>
              <p className="text-gray-500">Participants</p>
              <p>{challenge.participants} walkers</p>
            </div>
            <div>
              <p className="text-gray-500">
                {type === 'completed' ? 'Result' : type === 'active' ? 'Progress' : 'Type'}
              </p>
              <p>
                {type === 'completed' ? challenge.finalPosition : 
                 type === 'active' ? `${challenge.progress}% complete` : 
                 challenge.type.charAt(0).toUpperCase() + challenge.type.slice(1)}
              </p>
            </div>
          </div>
          
          {type === 'active' && (
            <div className="mt-3">
              <div className="h-2 w-full bg-gray-100 rounded-full">
                <div 
                  className={`h-full rounded-full ${
                    challenge.status === 'on-track' ? 'bg-primary' : 
                    challenge.status === 'behind' ? 'bg-red-500' : 
                    'bg-yellow-500'
                  }`}
                  style={{ width: `${challenge.progress}%` }}
                />
              </div>
            </div>
          )}
          
          <div className={`mt-4 flex ${type === 'completed' ? 'justify-between items-center' : 'justify-end'}`}>
            {type === 'completed' && (
              <div className="flex items-center">
                <Award size={16} className="text-accent mr-1" />
                <span className="text-sm">Reward: {challenge.reward}</span>
              </div>
            )}
            
            {type === 'available' ? (
              <button 
                className="bg-primary text-white px-4 py-2 rounded-md flex items-center"
                onClick={() => onJoin(challenge.id)}
              >
                <Plus size={16} className="mr-1" />
                Join Challenge
              </button>
            ) : (
              <button className="text-secondary flex items-center">
                View Details
                <ChevronRight size={16} />
              </button>
            )}
          </div>
          
          {isSpecialEvent && challenge.eventInfo && (
            <div className="mt-4 p-3 bg-accent bg-opacity-5 rounded-lg">
              <div className="flex items-center text-accent mb-1">
                <Calendar size={14} className="mr-1" />
                <span className="text-sm font-medium">Event Information</span>
              </div>
              <p className="text-sm">{challenge.eventInfo}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChallengeCard;